import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { toast } from 'react-toastify';
import styles from './ManageAccountPanel.module.scss';
import { useUserState } from '../../providers/user-state.provider.ts';
import { normalizeUserInfo, useUserService } from '../../services/user.service';
import { useSubscriptionStatus } from '../../providers/subscription-status.provider';
import router from '../../providers/router';
import { Routes } from '../../types/constants';
import { isSubscriptionNonTerminal } from '../../utils/subscription';

const formatDate = (value?: string | null): string | null => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

const formatFutureDate = (value?: string | null): string | null => {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return !Number.isNaN(date.getTime()) && date > new Date() ? date.toLocaleDateString() : null;
};

const getDeactivationError = (error: unknown): string => {
    const responseData = (error as {
        response?: { data?: string | { detail?: string; message?: string; title?: string } };
    })?.response?.data;

    if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
    }
    if (responseData && typeof responseData === 'object') {
        return responseData.detail ?? responseData.message ?? responseData.title
            ?? 'The account could not be deactivated. Please try again.';
    }
    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }
    return 'The account could not be deactivated. Please try again.';
};

const ManageAccountPanel = () => {
    const { getUserInfo, isLoggedIn, clearUserInfo, setUserInfo } = useUserState();
    const { login, refreshUser, deactivateAccount } = useUserService();
    const subscriptionStatus = useSubscriptionStatus();
    const [, , removeCookie] = useCookies(['user']);
    const userInfo = getUserInfo();
    const subscription = subscriptionStatus.subscription;
    const hasManagedSubscription = subscriptionStatus.hasActiveSubscription
        || isSubscriptionNonTerminal(subscription);
    const cancellationDate = formatFutureDate(subscription?.PlanCancelAt);
    const periodEndDate = formatDate(subscription?.PlanCurrentPeriodEnd);
    const isCancellationScheduled = subscription?.CancelAtPeriodEnd === true || cancellationDate !== null;
    const hasUncancelledSubscription = hasManagedSubscription && !isCancellationScheduled;

    const [showDialog, setShowDialog] = useState(false);
    const [password, setPassword] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const canDeactivate = useMemo(() => (
        isLoggedIn
        && !!userInfo?.userId
        && !subscriptionStatus.isSubscriptionLoading
        && !hasUncancelledSubscription
    ), [hasUncancelledSubscription, isLoggedIn, subscriptionStatus.isSubscriptionLoading, userInfo?.userId]);

    const handleConfirmDeactivate = async () => {
        if (!userInfo?.userId || !userInfo.token || !password || isSubmitting) {
            if (!password) {
                setFormError('Enter your current password to continue.');
            }
            return;
        }

        setIsSubmitting(true);
        setFormError(null);
        let refreshedUser;
        try {
            refreshedUser = await refreshUser(userInfo.token);
            if (refreshedUser.userId !== userInfo.userId) {
                throw new Error('Authenticated account changed.');
            }
            setUserInfo(refreshedUser);
        } catch {
            setFormError('Refresh the page, sign in again, and then retry account deactivation.');
            setIsSubmitting(false);
            return;
        }

        try {
            const response = normalizeUserInfo(await login(
                { username: refreshedUser.username, password },
                { suppressAuthModalOnUnauthorized: true }
            ));
            if (!response.userId || response.userId !== refreshedUser.userId) {
                setFormError('Current password is incorrect. Check it and try again.');
                setIsSubmitting(false);
                return;
            }
        } catch {
            setFormError('Current password is incorrect. Check it and try again.');
            setIsSubmitting(false);
            return;
        }

        try {
            await deactivateAccount();
            removeCookie('user');
            clearUserInfo();
            toast.success('Account deactivated.');
            router.navigate(Routes.HOME);
            setShowDialog(false);
            setPassword('');
        } catch (error: unknown) {
            setFormError(getDeactivationError(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isLoggedIn) {
        return null;
    }

    return (
        <>
            <section className={styles.section} aria-labelledby="deactivate-account-heading">
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle} id="deactivate-account-heading">Deactivate account</h2>
                    <p className={styles.cardCopy}>
                        Deactivation immediately stops you signing in and accessing CombatTV. It does not delete
                        your account data or alter a Stripe subscription.
                    </p>
                </div>

                {subscriptionStatus.isSubscriptionLoading ? (
                    <div className={styles.notice}>Checking your subscription before account deactivation...</div>
                ) : hasUncancelledSubscription ? (
                    <div className={styles.blockedProcess}>
                        <strong>Cancel your subscription before deactivating your account.</strong>
                        <ol>
                            <li>Open Billing and choose Manage subscription.</li>
                            <li>Schedule the cancellation in Stripe.</li>
                            <li>Return here to deactivate your account immediately, if you wish.</li>
                        </ol>
                        <Link className={styles.billingLink} to={Routes.PROFILE_BILLING}>
                            Go to Billing to cancel subscription
                        </Link>
                    </div>
                ) : (
                    <>
                        {isCancellationScheduled ? (
                            <div className={styles.scheduledNotice} role="status">
                                <strong>Your subscription cancellation is already scheduled.</strong>
                                <span>
                                    It will still end on {periodEndDate ?? cancellationDate ?? 'the date shown by Stripe'}.
                                    Deactivating now immediately removes your remaining CombatTV access.
                                </span>
                            </div>
                        ) : null}
                        <div className={styles.consequences}>
                            <strong>Before you deactivate:</strong>
                            <ul>
                                <li>You will be signed out and will no longer be able to access CombatTV.</li>
                                <li>Your profile and account history will be retained rather than deleted.</li>
                                <li>Contact CombatTV support if you later want the account reactivated.</li>
                            </ul>
                        </div>
                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.dangerButton}
                                onClick={() => setShowDialog(true)}
                                disabled={!canDeactivate}
                            >
                                Deactivate account
                            </button>
                        </div>
                    </>
                )}
            </section>

            {showDialog ? (
                <div className={styles.dialogOverlay} role="dialog" aria-modal="true" aria-labelledby="deactivate-account-title">
                    <div className={styles.dialog}>
                        <h3 className={styles.dialogTitle} id="deactivate-account-title">Deactivate your account?</h3>
                        <p className={styles.dialogCopy}>
                            This disables your login and CombatTV access immediately. It does not delete your data
                            or change your Stripe subscription. Enter your current password to confirm.
                        </p>
                        <label className={styles.fieldLabel} htmlFor="deactivate-account-password">Current password</label>
                        <input
                            id="deactivate-account-password"
                            className={styles.field}
                            type="password"
                            value={password}
                            onChange={(event) => {
                                setPassword(event.target.value);
                                setFormError(null);
                            }}
                            autoComplete="current-password"
                        />
                        {formError ? <span className={styles.error} role="alert">{formError}</span> : null}
                        <div className={styles.dialogActions}>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={() => {
                                    setShowDialog(false);
                                    setPassword('');
                                    setFormError(null);
                                }}
                            >
                                Keep account
                            </button>
                            <button
                                type="button"
                                className={styles.dangerButton}
                                onClick={handleConfirmDeactivate}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Deactivating...' : 'Deactivate account'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default ManageAccountPanel;
